import "@fontsource-variable/google-sans-code";
import "@fontsource-variable/lexend";
import PrimeTheme from "@primevue/themes/aura";
import { createCookies } from "@vueuse/integrations/useCookies";
import * as devalue from "devalue";
import { delay } from "es-toolkit";
import type express from "express";
import { createPinia, type Pinia } from "pinia";
import { FocusTrap, ToastService, Tooltip } from "primevue";
import PrimeVue, { type PrimeVueLocaleOptions } from "primevue/config";
import { type App, createSSRApp } from "vue";
import { createMemoryHistory, createRouter, createWebHistory, type Router } from "vue-router";
import RootApp from "./RootApp.vue";
import { Log } from "./common/logging.common";
import { keys } from "./data/keys";
import { routes } from "./data/routes";
import { useEnvironmentStore } from "./stores/environment.store";
import { useLoadingStore } from "./stores/loading.store";
import { useProfileStore } from "./stores/profile.store";
import { useSettingsStore } from "./stores/settings.store";
import { useTokenStore } from "./stores/token.store";
import "./styles/main.css";

function setupRouter() {
    if (import.meta.env.SSR) {
        return createRouter({
            history: createMemoryHistory(),
            routes: routes,
        });
    } else {
        return createRouter({
            history: createWebHistory(),
            routes: routes,
        });
    }
}

function setupLoadingState(app: App, router: Router, pinia: Pinia) {
    const loadingStore = useLoadingStore(pinia);
    app.provide(keys.isLoadingVisible, loadingStore.asRef());

    router.beforeEach(async (from, to) => {
        if (from.path !== to.path) {
            loadingStore.toggle(true);
        }

        if (!import.meta.env.SSR && import.meta.env.MODE === "development") {
            await delay(250);
        }
    });

    router.afterEach(async (_to, _from, failure) => {
        if (failure !== undefined) {
            console.error(failure);
        }

        loadingStore.toggle(false);
    });
}

export async function setupApp(url: string, req?: express.Request) {
    const pinia = createPinia();
    const router = setupRouter();

    const app = createSSRApp(RootApp)
        .use(router)
        .use(pinia)
        .use(PrimeVue, {
            theme: {
                preset: PrimeTheme,
                options: {
                    darkModeSelector: false,
                },
            },
            locale: {
                monthNames: [
                    "Januari",
                    "Februari",
                    "Maret",
                    "April",
                    "Mei",
                    "Juni",
                    "Juli",
                    "Agustus",
                    "September",
                    "Oktober",
                    "November",
                    "Desember",
                ],
                monthNamesShort: [
                    "Jan",
                    "Feb",
                    "Mar",
                    "Apr",
                    "Mei",
                    "Jun",
                    "Jul",
                    "Agu",
                    "Sep",
                    "Okt",
                    "Nov",
                    "Des",
                ],
            } as const satisfies Partial<PrimeVueLocaleOptions>,
        })
        .use(ToastService)
        .directive("tooltip", Tooltip)
        .directive("focustrap", FocusTrap);

    if (import.meta.env.SSR) {
        const tokenCookies = createCookies(req)(["token"]);
        const token = tokenCookies.get("token") ?? undefined;

        const environmentStore = useEnvironmentStore(pinia);
        environmentStore.inject(
            process.env.API_ENDPOINT as string,
            process.env.ASSETS_ENDPOINT as string,
        );

        if (
            url.match(
                /^\/(signup|join|payment|privacy-policy|terms-of-service|mobile-support)(?:\/(.+)?)?/,
            )
        ) {
            await router.push(url);
        } else if (token !== undefined) {
            const tokenStore = useTokenStore(pinia);
            const profileStore = useProfileStore(pinia);
            const settingsStore = useSettingsStore(pinia);
            tokenStore.inject(token);

            try {
                await profileStore.synchronize();
                await settingsStore.synchronize();
                await router.push(url);
            } catch (e) {
                if (e instanceof Error) {
                    Log.e(`${e.name}: ${e.message}`);
                } else {
                    Log.e(e);
                }

                tokenStore.clear();
                await router.push("/signin");
            }
        } else {
            await router.push("/signin");
        }
    } else {
        pinia.state.value = devalue.parse(window.__pinia);
        const tokenStore = useTokenStore(pinia);

        if (
            url.match(
                /^\/(signup|join|payment|privacy-policy|terms-of-service|mobile-support)(?:\/(.+)?)?/,
            )
        ) {
            await router.push(url);
        } else if (!tokenStore.isValid()) {
            await router.push("/signin");
        }
    }

    await router.isReady();
    setupLoadingState(app, router, pinia);
    return { app: app, router: router, pinia: pinia };
}

declare global {
    interface Window {
        __pinia: any;
    }
}
